/**
 * Channel Repository
 * Handles all channel-related API calls
 */

import { apiClient } from '../../client'
import type { PaginatedResponse } from '../role.repository'

export interface Channel {
    id: number
    tenant_id: number
    en_name: string
    ar_name?: string | null
    status: number
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date
}

export interface CreateChannelRequest {
    tenant_id: number
    en_name: string
    ar_name?: string
    status?: number
    // Note: created_by is automatically set by backend from logged-in user
}

export interface UpdateChannelRequest {
    tenant_id?: number
    en_name?: string
    ar_name?: string
    status?: number
    // Note: updated_by is automatically set by backend from logged-in user
}

class ChannelRepository {
    /**
     * Get all channels with pagination
     */
    async getAll(page: number = 1, limit: number = 10, filters?: any): Promise<PaginatedResponse<Channel>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }
            if (filters.channel_id) {
                params.append('channel_id', filters.channel_id)
            }
        }

        return apiClient.get<PaginatedResponse<Channel>>(`/channels?${params.toString()}`)
    }

    /**
     * Get channel by ID
     */
    async getById(id: number): Promise<Channel> {
        return apiClient.get<Channel>(`/channels/${id}`)
    }

    /**
     * Create a new channel
     */
    async create(data: CreateChannelRequest): Promise<Channel> {
        return apiClient.post<Channel>('/channels', data)
    }

    /**
     * Update an existing channel
     */
    async update(id: number, data: UpdateChannelRequest): Promise<Channel> {
        return apiClient.put<Channel>(`/channels/${id}`, data)
    }

    /**
     * Delete a channel
     */
    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/channels/${id}`)
    }

    /**
     * Export channels to Excel
     */
    async exportToExcel(filters?: any): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters?.search) {
            params.append('search', filters.search)
        }
        if (filters?.status) {
            params.append('status', filters.status)
        }
        if (filters?.channel_id) {
            params.append('channel_id', filters.channel_id.toString())
        }

        const baseUrl = `/channels/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' })
    }
}

// Export singleton instance
export const channelRepository = new ChannelRepository()

